/**
 * Status pills for the stocktake approval surface (DESIGN.md §13.1 geometry:
 * `ring-1`, never `border-`, EditingPill type scale).
 *
 * "Waiting on 3PL" is deliberately NOT amber. In this app amber means "needs
 * you"; a count sitting with the other side is the one state that does not,
 * so it takes the brand accent instead and drops out of the act-on-me bucket.
 * Each pill pairs its colour with a glyph so state never rides on hue alone
 * (DESIGN.md §16).
 */
import { Check, Clock, Loader2, RotateCcw, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { StocktakeProposalStatus } from '@threadheads/shared';

type PillTone = 'pending' | 'waiting' | 'applying' | 'applied' | 'rejected';

const TONE_BY_STATUS: Record<StocktakeProposalStatus, PillTone> = {
  pending: 'pending',
  waiting_on_3pl: 'waiting',
  // `approved` is the transient mid-apply claim: the decision is made and the
  // stock write is in flight. It is NOT "applied" — saying so while the write
  // is still running (or has failed and left the row stuck) would be a lie the
  // reviewer acts on. It carries the brand accent + a spinner instead, and
  // offers no actions anywhere.
  approved: 'applying',
  applied: 'applied',
  rejected: 'rejected',
};

const TONE_CLASS: Record<PillTone, string> = {
  pending: 'bg-amber-100 text-amber-800 ring-amber-300/80',
  waiting: 'bg-threadheads-purple/10 text-threadheads-purple ring-threadheads-purple/35',
  applying: 'bg-threadheads-purple/10 text-threadheads-purple ring-threadheads-purple/35',
  applied: 'bg-emerald-100 text-emerald-800 ring-emerald-300/80',
  rejected: 'bg-muted text-muted-foreground ring-border',
};

const TONE_ICON: Record<PillTone, typeof Check> = {
  pending: TriangleAlert,
  waiting: Clock,
  applying: Loader2,
  applied: Check,
  rejected: RotateCcw,
};

const TONE_LABEL_KEY: Record<PillTone, string> = {
  pending: 'stocktakes.pill.pending',
  waiting: 'stocktakes.pill.waiting',
  applying: 'stocktakes.pill.applying',
  applied: 'stocktakes.pill.applied',
  rejected: 'stocktakes.pill.rejected',
};

export function StocktakeStatusPill({
  status,
  className,
}: {
  status: StocktakeProposalStatus;
  className?: string;
}) {
  const { t } = useTranslation('inventory');
  const tone = TONE_BY_STATUS[status] ?? 'rejected';
  const Icon = TONE_ICON[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
        TONE_CLASS[tone],
        className,
      )}
    >
      <Icon
        className={cn('h-2.5 w-2.5', tone === 'applying' && 'motion-safe:animate-spin')}
        aria-hidden="true"
      />
      {t(TONE_LABEL_KEY[tone])}
    </span>
  );
}

/** Amber "N lines need a look" chip used on list rows and variance rows. */
export function FlagChip({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-amber-300/80',
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Brand-accent sibling of `FlagChip` for the waiting-on-them signal. */
export function WaitChip({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-threadheads-purple/10 px-1.5 py-0.5 text-[11px] font-medium text-threadheads-purple ring-1 ring-threadheads-purple/30',
        className,
      )}
    >
      {children}
    </span>
  );
}
